import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { convertFromUTC, convertToUTC } from '../../../util/convert_timezone';
import dateService from '../../../services/dateService';
import { isMultiLocation, isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';

const TARGET_ROLE_LABELS = {
  owner: 'Owner',
  manager: 'Manager',
  technician: 'Technician',
  front_desk: 'Front Desk'
};

const TARGET_ROLE_OPTIONS = [
  { value: '', label: 'Everyone' },
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'technician', label: 'Technician' },
  { value: 'front_desk', label: 'Front Desk' }
];

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' }
];

const SNOOZE_OPTIONS = [
  { label: 'In 1 hour', hours: 1 },
  { label: 'In 4 hours', hours: 4 },
  { label: 'Tomorrow morning', tomorrow: true },
  { label: 'Next week', days: 7 }
];

const PRIORITY_COLORS = {
  urgent: 'var(--color-danger)',
  high: 'var(--color-brand-orange)',
  normal: 'var(--color-info)',
  low: 'var(--color-gray-500)'
};

// Linkify phone numbers in text
const PHONE_REGEX = /(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g;

function renderWithPhoneLinks(text) {
  if (!text) return null;
  const parts = text.split(PHONE_REGEX);
  return parts.map((part, i) => {
    if (PHONE_REGEX.test(part)) {
      PHONE_REGEX.lastIndex = 0;
      const digits = part.replace(/[^\d+]/g, '');
      return (
        <a key={i} href={`tel:${digits}`} style={{ color: 'var(--color-info)', fontWeight: 600 }}>
          {part}
        </a>
      );
    }
    return part;
  });
}

class TaskDetail extends Component {
  state = {
    editing: false,
    editTitle: '',
    editNextAction: '',
    editDescription: '',
    editPriority: 'normal',
    editDueDate: null,
    editAssignedTo: '',
    editTargetRole: '',
    showSnoozeMenu: false,
    showDeleteConfirm: false
  };

  componentDidUpdate(prevProps) {
    if (prevProps.task?.task_id !== this.props.task?.task_id) {
      this.setState({ editing: false, showSnoozeMenu: false, showDeleteConfirm: false });
    }
  }

  startEditing = () => {
    const { task } = this.props;
    this.setState({
      editing: true,
      editTitle: task.title || '',
      editNextAction: task.next_action || '',
      editDescription: task.description || '',
      editPriority: task.priority || 'normal',
      editDueDate: task.due_date ? dateService.fromAPIDate(task.due_date).toDate() : null,
      editAssignedTo: task.assigned_to_user_id || task.assigned_to || '',
      editTargetRole: task.target_role || ''
    });
  };

  cancelEditing = () => {
    this.setState({ editing: false });
  };

  handleSave = () => {
    const { editTitle, editNextAction, editDescription, editPriority, editDueDate, editAssignedTo, editTargetRole } = this.state;
    this.props.onUpdate(this.props.task.task_id, {
      title: editTitle,
      next_action: editNextAction || null,
      description: editDescription,
      priority: editPriority,
      due_date: editDueDate ? moment(editDueDate).format('YYYY-MM-DD') : null,
      assigned_to: editAssignedTo || null,
      target_role: editTargetRole || null
    });
    this.setState({ editing: false });
  };

  handleSnooze = (option) => {
    let localTime;
    if (option.tomorrow) {
      localTime = moment().add(1, 'day').startOf('day').hour(8);
    } else if (option.days) {
      localTime = moment().add(option.days, 'days').startOf('day').hour(8);
    } else {
      localTime = moment().add(option.hours, 'hours');
    }
    const snoozeUntil = convertToUTC(localTime).format('YYYY-MM-DD HH:mm:ss');
    this.props.onSnooze(this.props.task.task_id, snoozeUntil);
    this.setState({ showSnoozeMenu: false });
  };

  handleEntityClick = () => {
    const { task } = this.props;
    if (task.entity_type === 'request') {
      this.props.history.push('/requestimates', { selectRequestId: task.entity_id });
    }
  };

  getStaffName(userId) {
    const { staffList } = this.props;
    if (!userId || !staffList) return null;
    const staff = staffList.find(s => String(s.User_id || s.user_id) === String(userId));
    return staff ? (staff.user_name || staff.name || `${staff.First_name || ''} ${staff.Last_name || ''}`.trim()) : null;
  }

  render() {
    const { task, onComplete, onDelete, user, staffList, locations, selectedLocationId } = this.props;
    const multiLoc = isMultiLocation(locations);
    const allLocView = isAllLocations(selectedLocationId);
    const { editing, showSnoozeMenu, showDeleteConfirm } = this.state;

    if (!task) return null;

    const localDueDate = task.due_date ? dateService.fromAPIDate(task.due_date) : null;
    const localCreatedAt = task.created_at ? convertFromUTC(task.created_at) : null;
    const localCompletedAt = task.completed_at ? convertFromUTC(task.completed_at) : null;
    const isOverdue = localDueDate && localDueDate.isBefore(moment(), 'day') && task.status !== 'completed';
    const isCompleted = task.status === 'completed';
    const assigneeName = task.assigned_to_name || this.getStaffName(task.assigned_to_user_id || task.assigned_to);
    const creatorName = task.created_by_name || this.getStaffName(task.created_by_user_id || task.created_by);
    const completedByName = task.completed_by_name || this.getStaffName(task.completed_by_user_id || task.completed_by);
    const priColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.normal;
    const loc = multiLoc && allLocView && task.location_id
      ? (locations || []).find(l => String(l.location_id) === String(task.location_id))
      : null;

    return (
      <div className="card text-center">
        <div className="card-body px-4 py-4">

          {/* Title + priority badge — red if overdue */}
          {editing ? (
            <div className="jv-form-group" style={{ maxWidth: '400px', margin: '0 auto' }}>
              <label className="jv-label">Title</label>
              <input
                type="text"
                className="form-control jv-input"
                value={this.state.editTitle}
                onChange={(e) => this.setState({ editTitle: e.target.value })}
                autoFocus
              />
            </div>
          ) : (
            <h5 className="mb-2" style={{ color: isOverdue ? 'var(--color-danger)' : undefined }}>
              {task.title}
              {' '}
              <span className="jv-badge jv-badge-pill" style={{ backgroundColor: priColor, color: 'var(--color-text-inverse)' }}>
                {(task.priority || 'normal').toUpperCase()}
              </span>
              {task.target_role && (
                <>
                  {' '}
                  <span className="jv-badge jv-badge-pill" style={{ backgroundColor: 'var(--color-gray-200)', color: 'var(--color-text-primary)' }}>
                    <i className="fa-sharp fa-regular fa-user mr-1" style={{ fontSize: 'var(--font-size-xs)' }} />
                    {TARGET_ROLE_LABELS[task.target_role] || task.target_role}
                  </span>
                </>
              )}
              {isCompleted && (
                <span className="jv-badge jv-badge-pill ml-1" style={{ backgroundColor: 'var(--color-success)', color: 'var(--color-text-inverse)' }}>
                  <i className="fa-sharp fa-solid fa-circle-check" /> Completed
                </span>
              )}
            </h5>
          )}

          {/* Entity contact info — name, phone, email */}
          {!editing && (task.entity_name || task.entity_phone || task.entity_email) && (
            <div className="mb-3">
              {task.entity_name && (
                <div><strong>{task.entity_name}</strong></div>
              )}
              {task.entity_phone && (
                <div>
                  <a href={`tel:${task.entity_phone.replace(/[^\d+]/g, '')}`} style={{ color: 'var(--color-info)' }}>
                    <i className="fa-sharp fa-regular fa-phone mr-1" />{task.entity_phone}
                  </a>
                </div>
              )}
              {task.entity_email && (
                <div>
                  <a href={`mailto:${task.entity_email}`} style={{ color: 'var(--color-info)' }}>
                    <i className="fa-sharp fa-regular fa-envelope mr-1" />{task.entity_email}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Next Action — primary content */}
          {editing ? (
            <div className="jv-form-group" style={{ maxWidth: '400px', margin: '0 auto' }}>
              <label className="jv-label">Action</label>
              <textarea
                className="form-control jv-input"
                rows={2}
                value={this.state.editNextAction}
                onChange={(e) => this.setState({ editNextAction: e.target.value })}
                placeholder="What to do — phone number, amount, details..."
              />
            </div>
          ) : task.next_action ? (
            <div className="mb-3 text-left" style={{
              backgroundColor: 'var(--color-bg-panel)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3) var(--space-4)',
              fontSize: 'var(--font-size-base)',
              lineHeight: 'var(--line-height-base)',
              maxWidth: '400px',
              margin: '0 auto var(--space-4)'
            }}>
              {renderWithPhoneLinks(task.next_action)}
            </div>
          ) : null}

          {/* Due Date */}
          {editing ? (
            <div className="jv-form-group" style={{ maxWidth: '300px', margin: '0 auto' }}>
              <label className="jv-label">Due Date</label>
              <DatePicker
                selected={this.state.editDueDate}
                onChange={(date) => this.setState({ editDueDate: date })}
                dateFormat="MMM d, yyyy"
                className="form-control jv-input"
                placeholderText="Select due date..."
                isClearable
              />
            </div>
          ) : localDueDate ? (
            <div className="mb-3">
              <span style={{ color: isOverdue ? 'var(--color-danger)' : 'var(--color-text-secondary)', fontWeight: isOverdue ? 600 : 500 }}>
                <i className="fa-sharp fa-regular fa-calendar mr-1" />
                {isOverdue ? 'Overdue: ' : 'Due: '}
                {localDueDate.format('MMM D, YYYY')}
              </span>
            </div>
          ) : null}

          {/* Assigned To */}
          {editing ? null : assigneeName ? (
            <div className="mb-3">
              <span style={{ color: 'var(--color-text-secondary)' }}>
                <i className="fa-sharp fa-regular fa-user mr-1" /> {assigneeName}
              </span>
            </div>
          ) : null}

          {/* Priority, Who is this for?, Assign To (edit mode) */}
          {editing && (
            <>
              <div className="row" style={{ maxWidth: '400px', margin: '0 auto var(--space-4)' }}>
                <div className="col-6">
                  <div className="jv-form-group">
                    <label className="jv-label">Priority</label>
                    <Form.Control
                      as="select"
                      value={this.state.editPriority}
                      onChange={(e) => this.setState({ editPriority: e.target.value })}
                    >
                      {PRIORITY_OPTIONS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </Form.Control>
                  </div>
                </div>
                <div className="col-6">
                  <div className="jv-form-group">
                    <label className="jv-label">Who is this for?</label>
                    <Form.Control
                      as="select"
                      value={this.state.editTargetRole}
                      onChange={(e) => this.setState({ editTargetRole: e.target.value, editAssignedTo: '' })}
                    >
                      {TARGET_ROLE_OPTIONS.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </Form.Control>
                  </div>
                </div>
              </div>
              <div className="jv-form-group" style={{ maxWidth: '300px', margin: '0 auto' }}>
                <label className="jv-label">Assign To</label>
                <Form.Control
                  as="select"
                  value={this.state.editAssignedTo}
                  onChange={(e) => this.setState({ editAssignedTo: e.target.value })}
                >
                  <option value="">All</option>
                  {(staffList || [])
                    .filter(s => {
                      if (!this.state.editTargetRole) return true;
                      const r = (s.role || '').toLowerCase();
                      if (this.state.editTargetRole === 'front_desk') return r !== 'technician' && r !== 'owner';
                      return r === this.state.editTargetRole;
                    })
                    .map(s => (
                      <option key={s.User_id || s.user_id} value={s.User_id || s.user_id}>
                        {s.user_name || s.name || `${s.First_name || ''} ${s.Last_name || ''}`.trim()}
                      </option>
                    ))}
                </Form.Control>
              </div>
            </>
          )}

          {/* Location badge */}
          {loc && (
            <div className="mb-3">
              <span className="jv-badge jv-badge-pill" style={{
                backgroundColor: getLocationBadgeColor(task.location_id, locations),
                color: 'var(--color-text-inverse)'
              }}>
                {getLocationName(loc)}
              </span>
            </div>
          )}

          {/* Entity Link — only for requests (navigates to job) */}
          {task.entity_type === 'request' && task.entity_id && (
            <div className="mb-3">
              <span
                style={{ color: 'var(--color-info)', cursor: 'pointer' }}
                onClick={this.handleEntityClick}
              >
                <i className="fa-sharp fa-regular fa-link mr-1" />
                Job #{task.entity_id}
              </span>
            </div>
          )}

          {/* Action buttons */}
          {!isCompleted && !editing && (
            <div className="jv-btn-group-spaced">
              <button
                type="button"
                className="jv-btn jv-btn-primary"
                onClick={() => onComplete(task.task_id, user.User_id)}
              >
                Complete
              </button>
              <div className="task-snooze-dropdown">
                <button
                  type="button"
                  className="jv-btn jv-btn-secondary"
                  onClick={() => this.setState({ showSnoozeMenu: !showSnoozeMenu })}
                >
                  Snooze
                </button>
                {showSnoozeMenu && (
                  <div className="task-snooze-menu">
                    {SNOOZE_OPTIONS.map((opt, i) => (
                      <button
                        key={i}
                        className="task-snooze-option"
                        onClick={() => this.handleSnooze(opt)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="jv-btn jv-btn-tertiary"
                onClick={this.startEditing}
              >
                Edit
              </button>

              <div className="jv-btn-group-divider">{'\u00A0'}</div>

              <button
                type="button"
                className="jv-btn jv-btn-danger"
                onClick={() => this.setState({ showDeleteConfirm: true })}
              >
                Delete
              </button>
            </div>
          )}

          {/* Delete confirmation */}
          {!isCompleted && !editing && showDeleteConfirm && (
            <div className="mt-2 d-flex justify-content-center align-items-center">
              <small className="text-danger mr-3">Delete this reminder?</small>
              <button type="button" className="jv-btn jv-btn-danger" onClick={() => onDelete(task.task_id)}>
                Yes, Delete
              </button>
              <button type="button" className="jv-btn jv-btn-tertiary" onClick={() => this.setState({ showDeleteConfirm: false })}>
                Cancel
              </button>
            </div>
          )}

          {/* Edit mode: context/description field */}
          {editing && (
            <div className="jv-form-group" style={{ maxWidth: '400px', margin: '0 auto' }}>
              <label className="jv-label">Context</label>
              <textarea
                className="form-control jv-input"
                rows={2}
                value={this.state.editDescription}
                onChange={(e) => this.setState({ editDescription: e.target.value })}
                placeholder="Background notes (optional)"
              />
            </div>
          )}

          {/* Edit mode buttons */}
          {editing && (
            <div className="jv-btn-group-spaced mt-3">
              <button type="button" className="jv-btn jv-btn-primary" onClick={this.handleSave}>
                Save
              </button>
              <button type="button" className="jv-btn jv-btn-tertiary" onClick={this.cancelEditing}>
                Cancel
              </button>
            </div>
          )}

          {/* Context / Description — muted, below actions */}
          {!editing && task.description && (
            <div className="text-left" style={{
              borderTop: '1px solid var(--color-border-light)',
              paddingTop: 'var(--space-3)',
              maxWidth: '400px',
              margin: 'var(--space-3) auto 0'
            }}>
              <small style={{ color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--letter-spacing-uppercase)' }}>
                Context
              </small>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', lineHeight: 'var(--line-height-base)', marginTop: 'var(--space-1)', marginBottom: 0 }}>
                {task.description}
              </p>
            </div>
          )}

          {/* Timestamps */}
          <div className="mt-3 mb-1">
            <small style={{ color: 'var(--color-text-muted)' }}>
              Created {localCreatedAt ? localCreatedAt.format('MMM D, YYYY h:mm A') : ''}
              {creatorName && ` by ${creatorName}`}
              {isCompleted && localCompletedAt && (
                <>
                  {' '}&middot; Completed {localCompletedAt.format('MMM D, YYYY h:mm A')}
                  {completedByName && ` by ${completedByName}`}
                </>
              )}
            </small>
          </div>

        </div>
      </div>
    );
  }
}

export default withRouter(TaskDetail);
